import {
  GeometryBarragem,
  useBarragemContext,
  ZAS
} from "../../../context";
import {
  Box,
  Card,
  CircularProgress,
  Grid2,
  TextField,
  ThemeProvider,
  Typography,
  useTheme,
} from "@mui/material";
import {
  googleSateliteHybrid,
  HidroLayer,
  HidroLayerGroup,
  HidroMap
} from "../../mapa";
import { ZasConfig } from "./zas";
import { divIcon, Marker } from "leaflet";
import { deepEqual } from "../../../core";
import { ListGeometryConfig } from "./geometries";
import { useEffect, useRef, useState } from "react";
import { ErrorModal, ErrorOnDialog, LoadingModal } from "../../modals";
import { RequestError, useApi } from "../../../services";
import { DocRef } from "../../../services/api/barragem";

export const EmpreendimentoConfig: React.FC = () => {
  const api = useApi();
  const theme = useTheme();
  const { barragem, setBarragem } = useBarragemContext();

  const dadosReferenciar = ["nomeLatLon", "centroMun", "distMaxSirenes", "distMaxPercorrida", "polyZas", "polyZss", "uniSensivel", "rotasFuga"];
  const referenciasTitles = ["Nome, Lat, Lon", "Centro do Munípio", "Distância Max. Sirenes", "Distância Max. Percorrida", "Polígono da ZAS", "Polígono da ZSS", "Unidades Sensíveis", "Rotas de Fuga"];

  const [error, setError] = useState<RequestError>();
  const [zas, setZas] = useState<ZAS | RequestError>();
  const [geometries, setGeometries] = useState<GeometryBarragem[]>([]);
  const [barragemData, setBarragemData] = useState<{ nome: string; lat: number; lon: number; }>();
  const [docsRefs, setDocsRefs] = useState<DocRef[]>();
  const [messageLoading, setMessageLoading] = useState<string>();

  const timerDocsRefs = useRef<NodeJS.Timeout>();
  const layerBarragem = useRef<HidroLayerGroup>(new HidroLayerGroup({
    name: "Barragem",
    showing: true,
    layers: [],
  }));

  const paddingVal = 1;
  const heightGeom = 50;
  const iconSizeGeom = 20;
  const contrastText = theme.palette.getContrastText(theme.palette.background.default);

  // Atualizar barragem
  useEffect(() => {
    if (!barragem || "error" in barragem) return;

    if (!deepEqual(barragemData, barragem)) {
      setBarragemData({
        nome: barragem.nome,
        lat: barragem.lat,
        lon: barragem.lon,
      });
    }

    if (!deepEqual(barragem.geometries, geometries)) {
      setGeometries(barragem.geometries);
    }

    if (!deepEqual(zas, barragem.zas)) {
      setZas(barragem.zas);
    }
  }, [barragem]);

  // Documentos de referência
  useEffect(() => {
    if (!barragem || "error" in barragem) return;

    if (timerDocsRefs.current) clearTimeout(timerDocsRefs.current);

    timerDocsRefs.current = setTimeout(() => {
      api.barragem.getBarragemDocRef(barragem.id).then((val) => {
        if ("error" in val) {
          setError(val);
          return;
        }


        for (let i = 0; i < barragem.geometries.length; i++) {
          const estrutura = val.filter(val => val.dado === `estrutura_${i}`)[0];
          const brecha = val.filter(val => val.dado === `brecha_${i}`)[0];

          if (!estrutura) {
            val.push({
              id: 0,
              dado: `estrutura_${i}`,
              documento: "",
              idBarragem: barragem.id,
              dataCadastro: new Date(Date.now()),
              idUpdate: "",
            });
          }
          if (!brecha) {
            val.push({
              id: 0,
              dado: `brecha_${i}`,
              documento: "",
              idBarragem: barragem.id,
              dataCadastro: new Date(Date.now()),
              idUpdate: "",
            });
          }
        }

        val = val.filter(val => {
          if (!val.dado.startsWith("estrutura_") && !val.dado.startsWith("brecha_")) {
            return true;
          }
          return Number(val.dado.split("_")[1]) < barragem.geometries.length;
        });

        for (const dado of dadosReferenciar) {
          const doc = val.filter(val => val.dado === dado)[0];
          if (!doc) {
            val.push({
              id: 0, dado: dado, documento: "",
              idBarragem: barragem.id, idUpdate: "",
              dataCadastro: new Date(Date.now()),
            });
          }
        }

        const equalsItems = val.map(val => deepEqual(val, (docsRefs ?? []).filter(filterVal => val.dado === filterVal.dado)[0]));
        if (equalsItems.filter(val => !val).length > 0) {
          setDocsRefs([...val]);
        }
      });
    }, 100);

    return () => {
      if (timerDocsRefs.current) clearTimeout(timerDocsRefs.current);
    };

  }, [barragem]);

  // Layer da barragem
  useEffect(() => {
    if (!barragem || "error" in barragem) return;

    layerBarragem.current.clearLayers();
    try {
      const layerName = new Marker([barragem.lat ?? 0, barragem.lon ?? 0]);
      const component: JSX.Element = (
        <ThemeProvider theme={theme}>
          <div style={{
            height: `${heightGeom}px`,
            width: "250px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}>
            <Typography
              color="background.paper"
              sx={{ textShadow: `2px 2px 6px ${contrastText}` }}>
              {barragem.nome}
            </Typography>
          </div>
        </ThemeProvider>
      );
      const icon = divIcon({
        html: window.JSXToHTML(component),
        className: 'custom-svg-icon',
        iconAnchor: [iconSizeGeom / 2, heightGeom / 2],
      });

      layerName.setIcon(icon);

      layerBarragem.current.push(new HidroLayer({
        name: barragem.nome,
        showing: true,
        layer: layerName
      }));

      layerBarragem.current.push(new HidroLayer({
        name: "Google Satelite",
        showing: true,
        layer: googleSateliteHybrid(),
      }));

      layerBarragem.current.centerBounds();
    } catch {
      return;
    }
  }, [barragem]);

  // Atualizar dados da barragem
  useEffect(() => {
    if (!barragem || "error" in barragem) return;

    // Atualizar os dados da barragem
    if (barragemData && !deepEqual(barragemData, barragem)) {
      api.barragem.updateBarragem(
        barragem.id,
        barragemData.nome,
        barragemData.lat,
        barragemData.lon
      ).then((value) => {
        if ("error" in value) {
          setError(value);
          return;
        }

        setBarragem({
          ...barragem,
          nome: value.nome,
          lat: value.lat,
          lon: value.lon,
        });
      });
    }
  }, [barragemData]);

  // Atualizar dados das geometrias
  useEffect(() => {
    if (!barragem || "error" in barragem) return;

    const timer = setTimeout(() => {
      // Atualizar os dados das geometrias
      if (geometries.length != barragem.geometries.length || !deepEqual(geometries, barragem.geometries)) {
        setBarragem({ ...barragem, geometries: geometries });
        if (!docsRefs) return;

        for (let i = 0; i < geometries.length; i++) {
          const estrutura = docsRefs.filter(val => val.dado === `estrutura_${i}`)[0];
          const brecha = docsRefs.filter(val => val.dado === `brecha_${i}`)[0];

          if (!estrutura) {
            docsRefs.push({
              id: 0,
              dado: `estrutura_${i}`,
              documento: "",
              idBarragem: barragem.id,
              dataCadastro: new Date(Date.now()),
              idUpdate: "",
            });
          }
          if (!brecha) {
            docsRefs.push({
              id: 0,
              dado: `brecha_${i}`,
              documento: "",
              idBarragem: barragem.id,
              dataCadastro: new Date(Date.now()),
              idUpdate: "",
            });
          }
        }

        const newDocs = docsRefs.filter(val => {
          if (val.dado.startsWith("estrutura_") || val.dado.startsWith("brecha_")) {
            return Number(val.dado.split("_")[1]) < geometries.length;
          }
          return true;
        });

        setDocsRefs(newDocs);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [geometries]);

  // Atualizar dados da ZAS
  useEffect(() => {
    if (!barragem || "error" in barragem) return;

    // Atualização da zas
    if (
      barragem.zas && !("error" in barragem.zas) &&
      zas && !("error" in zas) &&
      !deepEqual(barragem.zas, zas)
    ) {
      setMessageLoading("Atualizando ZAS 0%");

      api.zas.updateZas(barragem.zas.id, zas, (loaded, total) => {
        setMessageLoading(`Atualizando ZAS ${Math.round(loaded * 100 / total)}%`);
      }).then((value) => {
        if ("error" in value) {
          setError(value);
          setMessageLoading(undefined);
          return;
        }
        setBarragem({ ...barragem, zas: zas });
        setMessageLoading(undefined);
      });

      setMessageLoading(undefined);
    }
  }, [zas]);

  // Atualizar dados dos documentos de referência
  useEffect(() => {
    if (!barragem || "error" in barragem) return;
    if (!docsRefs) return;

    if (timerDocsRefs.current) clearTimeout(timerDocsRefs.current);

    timerDocsRefs.current = setTimeout(() => {
      api.barragem.updateBarragemDocRef(barragem.id, docsRefs).then(val => {
        if ("error" in val) {
          setError(val);
          return;
        }

        const equalsItems = val.map(val => {
          const refs = docsRefs.filter(filterVal => val.dado === filterVal.dado)[0];
          val.dataModificacao = undefined;
          if (refs) refs.dataModificacao = undefined;
          return deepEqual(val, refs);
        });

        if (equalsItems.filter(val => !val).length > 0) {
          setDocsRefs(val);
        }

      });
    }, 100);

    return () => {
      if (timerDocsRefs.current) clearTimeout(timerDocsRefs.current);
    };
  }, [docsRefs]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!barragemData) return;

    const { name, value } = event.target;
    setBarragemData({
      ...barragemData,
      [name]: value,
    });
  };

  if (!barragemData) {
    return <LoadingModal open={!barragemData} />;
  }

  if (barragem && "error" in barragem) {
    return <Box sx={{ padding: "15px" }}>
      < ErrorModal {...barragem} />
    </Box>;
  }

  return (
    <>
      {/* Loading de atualização */}
      {messageLoading && <LoadingModal
        open={Boolean(messageLoading)}
        message={messageLoading}
      />}

      {/* Erro ao atualizar os dados */}
      <ErrorOnDialog
        open={Boolean(error)}
        error={error?.error ?? ""}
        message={error?.message ?? ""}
        onClose={() => setError(undefined)}
      />

      {/* Dados */}
      <Box sx={{
        gap: "15px",
        width: "100%",
        display: "flex",
        fontSize: "1em",
        overflowY: "auto",
        flexDirection: "column",
        padding: `${paddingVal}em`,
      }}>

        {/* Nome da Barragem e localização */}
        <Card sx={{
          padding: "15px",
          display: "flex",
          minHeight: "226px",
          flexDirection: "row",
          // height: "200px",
          width: "100%",
          gap: "15px",
        }}>
          {/* Dados */}
          {barragemData &&
            <div style={{
              gap: "0.5em",
              width: "45%",
              display: "flex",
              flexDirection: "column",
            }}>
              {/* Espaçador */}
              <div style={{ flex: 1 }} />

              {/* Nome da Barragem */}
              <TextField
                fullWidth
                label="Nome da Estrutura"
                name="nome"
                value={barragemData.nome}
                onChange={onChange}
                variant="outlined"
              />

              {/* Localização da Barragem */}
              <Box sx={{
                display: "flex",
                flexDirection: "row",
              }}>
                {/* Lat e Lon */}
                <TextField
                  fullWidth
                  label="Latitude"
                  name="lat"
                  value={(barragemData.lat ?? 0).toString().replaceAll(".", ",")}
                  onChange={(value) => {
                    value.target.value = value.target.value.replaceAll(",", ".");
                    onChange(value);
                  }}
                  variant="outlined"
                />

                <div style={{ width: '15px' }} />

                <TextField
                  fullWidth
                  label="Longitude"
                  name="lon"
                  value={(barragemData.lon ?? 0).toString().replaceAll(".", ",")}
                  onChange={(value) => {
                    value.target.value = value.target.value.replaceAll(",", ".");
                    onChange(value);
                  }}
                  variant="outlined"
                />
              </Box>

              {/* Documentos de referência */}
              <>
                <Typography>Documentos de Referência:</Typography>

                <Grid2 container sx={{
                  gap: "0.5em",
                  width: "100%",
                  fontSize: "1em",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  {dadosReferenciar.map((keyDado, i) => {
                    return <Grid2
                      key={i}
                      size={5.4}
                    >
                      <TextField
                        fullWidth
                        label={referenciasTitles[i]}
                        value={((docsRefs ?? []).filter(val => val.dado === keyDado)[0]?.documento) ?? ""}
                        onChange={(event) => setDocsRefs([...(docsRefs ?? []).map(val => {
                          if (val.dado === keyDado) val.documento = event.target.value;
                          return val;
                        })])}
                        variant="outlined"
                      />
                    </Grid2>;
                  })}

                  {barragem?.geometries.map((geom, i) => {
                    const docRefEstrutura = (docsRefs ?? []).filter(val => val.dado === `estrutura_${i}`)[0];
                    const docRefBrecha = (docsRefs ?? []).filter(val => val.dado === `brecha_${i}`)[0];
                    return <Grid2 size={11} key={i} sx={{
                      gap: "0.5em",
                      display: "flex",
                      flexDirection: "row",
                    }}>

                      <TextField
                        fullWidth
                        label={`${i + 1}° Estrutura`}
                        value={docRefEstrutura?.documento ?? ""}
                        onChange={(event) => setDocsRefs([...(docsRefs ?? []).map(val => {
                          if (val.dado === `estrutura_${i}`) val.documento = event.target.value;
                          return val;
                        })])}
                        variant="outlined"
                      />
                      <TextField
                        fullWidth
                        label={`${i + 1}° Brecha`}
                        value={docRefBrecha?.documento ?? ""}
                        onChange={(event) => setDocsRefs([...(docsRefs ?? []).map(val => {
                          if (val.dado === `brecha_${i}`) val.documento = event.target.value;
                          return val;
                        })])}
                        variant="outlined"
                      />

                    </Grid2>;
                  })}

                </Grid2 >

              </>
            </div>
          }

          {/* Mapa */}
          <div style={{ flex: 1 }}>
            <HidroMap
              id="location-map"
              animateStart={false}
              hidroLayers={layerBarragem.current}
            />
          </div>
        </Card>

        {/* Estruturas */}
        <Card>
          {barragem &&
            <ListGeometryConfig
              barragem={barragem}
              onChange={setGeometries}
            />
          }
        </Card>

        {/* ZAS */}
        <Card>
          {!zas && < Box sx={{
            width: "100%",
            height: "176px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <CircularProgress />
          </Box>}

          {zas && "error" in zas && <ErrorModal error={zas.error} message={zas.message} />}

          {zas && !("error" in zas) && <ZasConfig
            zas={zas}
            onChange={setZas}
          />}
        </Card >
      </Box >
    </>);
};
